import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { Tooltip } from '../UI';
import styles from './HeaderNav.module.css';

export interface NavItem {
  labelKey: string;
  // null when the destination is location-scoped and no location has resolved
  // yet: the item renders disabled rather than linking nowhere.
  to: string | null;
  isAdminOnly?: boolean;
  // Match the path exactly instead of by prefix. Needed for the home route,
  // which is a prefix of every location-scoped page and would otherwise always
  // render as the active item.
  isExactMatch?: boolean;
}

interface HeaderNavItemProps {
  item: NavItem;
  className?: string;
  onNavigate?: () => void;
}

// One navigation destination. A destination the current user may not reach at
// all is filtered out before this renders; only the "no location resolved yet"
// case shows here, disabled with a tooltip explaining what unblocks it.
export const HeaderNavItem = ({ item, className, onNavigate }: HeaderNavItemProps) => {
  const { t } = useTranslation();
  const itemClass = `${styles.navLink} ${className ?? ''}`;

  return (
    <li>
      {item.to ? (
        <NavLink
          to={item.to}
          end={item.isExactMatch}
          onClick={onNavigate}
          className={({ isActive }) => `${itemClass} ${isActive ? styles.navLinkActive : ''}`}
        >
          {t(item.labelKey)}
        </NavLink>
      ) : (
        <Tooltip
          trigger={
            // A button styled as a nav item rather than a link: there is no
            // address to navigate to yet. aria-disabled instead of disabled
            // keeps it focusable, which is what lets the tooltip explain why.
            <button
              type="button"
              className={`${itemClass} ${styles.navLinkDisabled}`}
              aria-disabled="true"
            >
              {t(item.labelKey)}
            </button>
          }
          content={<span className={styles.navTooltip}>{t('nav-requires-location')}</span>}
        />
      )}
    </li>
  );
};
